import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { merge, debounceTime, Subscription } from 'rxjs';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import { InventoryService } from '../../services/inventory.service';
import { PlantMetadata } from '../../models/stock-movement-metadata.model';
import { AlertService, AlertType, Alert } from '@michelin/theme';

@Component({
  selector: 'app-stock-management',
  templateUrl: './stock-management.component.html',
  styleUrls: ['./stock-management.component.scss', '../../inventory-shared.scss'],
  standalone: false
})
export class StockManagementComponent implements OnInit, OnDestroy {
  public translationService = inject(TranslationService);
  private fb = inject(FormBuilder);
  private inventoryService = inject(InventoryService);
  private alertService = inject(AlertService);

  private _selectedAction: 'receiveInventory' | 'adjustInventory' | null = null;

  get selectedAction(): 'receiveInventory' | 'adjustInventory' | null {
    return this._selectedAction;
  }

  set selectedAction(value: 'receiveInventory' | 'adjustInventory' | null) {
    this._selectedAction = value;
    this.updateQuantityValidators();
    this.clearForm();
  }

  plantOptions: PlantMetadata[] = [];
  plantOptionsFormatted: string[] = [];
  plantCodeToFkMap: { [plantCode: string]: string } = {};
  rawMaterialTypeOptions: string[] = [];
  rawMaterialStockMap: { [plantFk: string]: string[] } = {};
  rawMaterialStockOptions: string[] = [];
  stockMovementTypeOptions: string[] = [];
  serialNumbersByPlant: { [plantFk: string]: string[] } = {};
  serialNumberOptions: string[] = [];
  loadingStockData = false;
  loadingRawMaterials = false;
  rawMaterialsList: any[] = [];
  isPatchingFromResponse = false;

  receiveInventoryForm: FormGroup = this.fb.group({
    plant: ['', Validators.required],
    rawMaterialType: ['', Validators.required],
    serialNumber: ['', Validators.required],
    description: ['', Validators.required],
    totalAvailableStock: [{ value: '', disabled: true }],
    quantityToReceive: [null, [Validators.required, Validators.pattern('^\\d*\\.?\\d+$'), Validators.min(0)]],
    comment: ['']
  });

  private plantTypeSub: Subscription | null = null;
  private descSub: Subscription | null = null;
  private serialSub: Subscription | null = null;
  private quantitySub: Subscription | null = null;

  ngOnInit(): void {
    this.loadMetadata();
    this.subscribeToFormChanges();
    this.updateQuantityValidators();
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    this.unsubscribeFromFormChanges();

    // Reset form to clear validation errors
    this.receiveInventoryForm.reset();
  }

  private subscribeToFormChanges(): void {
    this.unsubscribeFromFormChanges();
    this.plantTypeSub = merge(
      this.receiveInventoryForm.get('plant')!.valueChanges,
      this.receiveInventoryForm.get('rawMaterialType')!.valueChanges
    )
      .pipe(debounceTime(200))
      .subscribe(() => {
        if (this.isPatchingFromResponse) return;
        const plantCode = this.receiveInventoryForm.get('plant')!.value;
        const plantFk = this.plantCodeToFkMap[plantCode] || '';
        this.receiveInventoryForm.patchValue(
          {
            serialNumber: '',
            description: '',
            totalAvailableStock: '',
            quantityToReceive: null,
            comment: ''
          },
          { emitEvent: false }
        );
        this.updateSerialNumberOptions(plantFk);
        this.updateRawMaterialStockOptions(plantFk);
        this.fetchRawMaterials();
      });
    this.descSub = this.receiveInventoryForm
      .get('description')!
      .valueChanges.pipe(debounceTime(200))
      .subscribe((desc) => {
        if (this.isPatchingFromResponse) return;
        if (desc) {
          this.receiveInventoryForm.patchValue({ serialNumber: '' }, { emitEvent: false });
          this.fetchRawMaterials(true, false);
        }
      });
    this.serialSub = this.receiveInventoryForm
      .get('serialNumber')!
      .valueChanges.pipe(debounceTime(200))
      .subscribe((sn) => {
        if (this.isPatchingFromResponse) return;
        if (sn) {
          this.receiveInventoryForm.patchValue({ description: '' }, { emitEvent: false });
          this.fetchRawMaterials(true, false);
        }
      });
    // Subscribe to quantity changes to auto-cap at 99999.99
    this.quantitySub = this.receiveInventoryForm.get('quantityToReceive')!.valueChanges.subscribe((value) => {
      if (this.isPatchingFromResponse) return;
      if (value !== null && value !== undefined && value !== '') {
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (!isNaN(numValue) && numValue > 99999999.99) {
          this.receiveInventoryForm.patchValue({ quantityToReceive: 99999999.99 }, { emitEvent: false });
        } else if (!isNaN(numValue) && numValue < -99999999.99 && this._selectedAction === 'adjustInventory') {
          this.receiveInventoryForm.patchValue({ quantityToReceive: -99999999.99 }, { emitEvent: false });
        }
      }
    });
  }

  private unsubscribeFromFormChanges(): void {
    if (this.plantTypeSub) {
      this.plantTypeSub.unsubscribe();
      this.plantTypeSub = null;
    }
    if (this.descSub) {
      this.descSub.unsubscribe();
      this.descSub = null;
    }
    if (this.serialSub) {
      this.serialSub.unsubscribe();
      this.serialSub = null;
    }
    if (this.quantitySub) {
      this.quantitySub.unsubscribe();
      this.quantitySub = null;
    }
  }

  /**
   * Update validators for quantityToReceive based on selected action.
   * - Receive Inventory: Only positive values allowed (including 0), max 99999.99
   * - Adjust Inventory: Both positive and negative values allowed, max 99999.99
   */
  private updateQuantityValidators(): void {
    const quantityControl = this.receiveInventoryForm.get('quantityToReceive');

    if (this._selectedAction === 'adjustInventory') {
      // Allow negative values for adjustments (pattern allows optional minus sign)
      quantityControl?.setValidators([
        Validators.required,
        // Allows negative decimals
        Validators.pattern('^-?\\d*\\.?\\d+$'),
        Validators.max(99999999.99),
        Validators.min(-99999999.99)
      ]);
    } else {
      // Only positive values (including 0) for receiving inventory
      // Add custom validator to prevent '-' character
      const noNegativeSignValidator = (control: any) => {
        const value = control.value;
        if (value && typeof value === 'string' && value.includes('-')) {
          return { containsNegativeSign: true };
        }
        return null;
      };

      quantityControl?.setValidators([
        Validators.required,
        Validators.pattern('^\\d*\\.?\\d+$'), // Only positive decimals
        Validators.max(99999999.99),
        noNegativeSignValidator
      ]);
    }

    quantityControl?.updateValueAndValidity();
  }

  loadMetadata(): void {
    this.inventoryService.getStockMovementMetadata().subscribe({
      next: (response) => {
        const data = response.data;
        this.plantOptions = data?.plants || [];

        // Create plant code to FK mapping and formatted options for dropdown
        this.plantCodeToFkMap = {};
        const plantCodes = this.plantOptions.map((plant) => {
          this.plantCodeToFkMap[plant.plantCode] = plant.plantSetupPk;
          return plant.plantCode;
        });
        // Remove duplicates from plant codes
        this.plantOptionsFormatted = [...new Set(plantCodes)];

        this.rawMaterialTypeOptions = data?.rawMaterialType || [];
        this.rawMaterialStockMap = data?.rawMaterialStock;
        this.rawMaterialStockOptions = Object.values(this.rawMaterialStockMap || {}).reduce(
          (acc, stocks) => acc.concat(stocks),
          [] as string[]
        );
        this.stockMovementTypeOptions = data?.stockMovementType || [];
        this.serialNumbersByPlant = data?.serialNumbersByPlant || {};
        this.serialNumberOptions = Object.values(this.serialNumbersByPlant).reduce((acc, sns) => acc.concat(sns), [] as string[]);
        this.setDefaultValues();
      },
      error: (error) => console.error('Error loading stock management metadata:', error)
    });
  }

  /**
   * Fetch raw materials and update dropdowns or only dependent fields.
   * @param updateDropdowns If true, update dropdowns. If false, only update dependent fields.
   * @param updateDropdownsOnPlantChange If true, update dropdowns (default: true). If false, only update fields (used for desc/serial change).
   */
  fetchRawMaterials(updateDropdowns: boolean = true, updateDropdownsOnPlantChange: boolean = true): void {
    this.loadingRawMaterials = true;
    const { rawMaterialType, plant, serialNumber, description } = this.receiveInventoryForm.value;
    const plantFk = this.plantCodeToFkMap[plant] || plant || '';
    if (!rawMaterialType || !plantFk) {
      if (updateDropdowns) {
        this.serialNumberOptions = [];
        this.rawMaterialStockOptions = [];
      }
      this.rawMaterialsList = [];
      return;
    }
    this.inventoryService.getRawMaterials(rawMaterialType, plantFk, serialNumber, description).subscribe({
      next: (response) => {
        const rawMaterials = response.data || [];
        this.rawMaterialsList = rawMaterials;
        if (updateDropdowns && updateDropdownsOnPlantChange) {
          this.serialNumberOptions = Array.from(new Set(rawMaterials.map((rm) => rm.serialNum).filter(Boolean))).sort();
          this.rawMaterialStockOptions = Array.from(new Set(rawMaterials.map((rm) => rm.rawMaterial).filter(Boolean))).sort();
        }
        this.updateFieldsFromRawMaterial();
        this.loadingRawMaterials = false;
      },
      error: () => {
        if (updateDropdowns && updateDropdownsOnPlantChange) {
          this.serialNumberOptions = [];
          this.rawMaterialStockOptions = [];
        }
        this.rawMaterialsList = [];
        this.loadingRawMaterials = false;
      }
    });
  }

  setDefaultValues(): void {
    const defaultPlantCode = this.plantOptionsFormatted[0] || '';
    const defaultPlantFk = this.plantCodeToFkMap[defaultPlantCode] || '';
    const defaultRawMaterialType = this.rawMaterialTypeOptions[0] || '';
    this.receiveInventoryForm.patchValue({ plant: defaultPlantCode, rawMaterialType: defaultRawMaterialType });
    this.serialNumberOptions = [];
    this.rawMaterialStockOptions = [];
    this.updateSerialNumberOptions(defaultPlantFk);
  }

  // Add/update this method for serial number options
  updateSerialNumberOptions(plantFk: string): void {
    if (plantFk) {
      this.serialNumberOptions = (this.serialNumbersByPlant[plantFk] || []).slice().sort();
    } else {
      this.serialNumberOptions = Object.values(this.serialNumbersByPlant)
        .reduce((acc, sns) => acc.concat(sns), [] as string[])
        .sort();
    }
  }

  // Add/update this method for raw material stock options
  updateRawMaterialStockOptions(plantFk: string): void {
    if (plantFk) {
      this.rawMaterialStockOptions = (this.rawMaterialStockMap[plantFk] || []).slice().sort();
    } else {
      this.rawMaterialStockOptions = Object.values(this.rawMaterialStockMap || {})
        .reduce((acc, stocks) => acc.concat(stocks), [] as string[])
        .sort();
    }
  }

  // Remove unused fetchStockData method

  // Add/update this method for clearing the form
  clearForm(): void {
    this.receiveInventoryForm.reset({
      plant: '',
      rawMaterialType: '',
      serialNumber: '',
      description: '',
      totalAvailableStock: '',
      quantityToReceive: null,
      comment: ''
    });
    // Clear all validation errors by marking as untouched and pristine
    this.receiveInventoryForm.markAsUntouched();
    this.receiveInventoryForm.markAsPristine();
    this.serialNumberOptions = [];
    this.rawMaterialStockOptions = [];
    this.setDefaultValues();
  }

  /**
   * Synchronize form fields with available raw material options.
   * Ensures that if the form has a value for description or serial number, and that value exists in the options, it is set and shown as selected.
   * If not, the field is cleared.
   */
  updateFieldsFromRawMaterial(): void {
    const serial = this.receiveInventoryForm.get('serialNumber')!.value;
    const desc = this.receiveInventoryForm.get('description')!.value;
    // Check if current form values exist in the options
    const serialValid = serial && this.serialNumberOptions.includes(serial);
    const descValid = desc && this.rawMaterialStockOptions.includes(desc);
    let match = null;
    if (serialValid && descValid) {
      match = this.rawMaterialsList.find((rm) => rm.serialNum === serial && rm.rawMaterial === desc);
    } else if (serialValid) {
      match = this.rawMaterialsList.find((rm) => rm.serialNum === serial);
    } else if (descValid) {
      match = this.rawMaterialsList.find((rm) => rm.rawMaterial === desc);
    }
    // If the value is not valid, clear it
    const patch: any = {};
    if (!serialValid) patch.serialNumber = '';
    if (!descValid) patch.description = '';
    if (match) {
      patch.serialNumber = match.serialNum || '';
      patch.description = match.rawMaterial || '';
      patch.totalAvailableStock = match.qtyUnit ? `${match.qty} ${match.qtyUnit}` : match.qty;
      patch.quantityToReceive = 0;
      patch.comment = '';
    } else {
      patch.totalAvailableStock = '';
    }
    this.receiveInventoryForm.patchValue(patch, { emitEvent: false });
  }

  submitForm(): void {
    if (this.receiveInventoryForm.valid) {
      this.loadingStockData = true;
      this.receiveInventoryForm.disable();
      const formValues = this.receiveInventoryForm.getRawValue();
      const plantFk = this.plantCodeToFkMap[formValues.plant] || formValues.plant || '';
      // Find the selected raw material object from the cached list
      let match = null;
      if (formValues.serialNumber && formValues.description) {
        match = this.rawMaterialsList.find((rm) => rm.serialNum === formValues.serialNumber && rm.rawMaterial === formValues.description);
      } else if (formValues.serialNumber) {
        match = this.rawMaterialsList.find((rm) => rm.serialNum === formValues.serialNumber);
      } else if (formValues.description) {
        match = this.rawMaterialsList.find((rm) => rm.rawMaterial === formValues.description);
      }
      const payload: any = {
        // Only include fields present on the form
        plantSetupFk: plantFk,
        rawMaterialType: formValues.rawMaterialType,
        serialNumber: formValues.serialNumber,
        rawMaterial: formValues.description,
        qty: formValues.quantityToReceive,
        qtyUnit: formValues.qtyUnit,
        comment: formValues.comment,
        stockMovementType: this._selectedAction === 'receiveInventory' ? 'RECEPTION' : 'ADJUSTMENT'
      };
      if (match && match.rawMaterialPk) {
        payload.rawMaterialFk = match.rawMaterialPk;
      }
      this.inventoryService.submitStockMovement(payload).subscribe({
        next: (response) => {
          // Show success alert
          if (this._selectedAction === 'receiveInventory') {
            this.alertService.showAlert({
              type: AlertType.success,
              content: this.translationService.translate('inventoryAddedSuccessfullyText'),
              timestamp: 60000
            } as Alert);
          } else if (this._selectedAction === 'adjustInventory') {
            this.alertService.showAlert({
              type: AlertType.success,
              content: this.translationService.translate('inventoryAdjustedSuccessfullyText'),
              timestamp: 60000
            } as Alert);
          }
          if (response && response.data) {
            // Unsubscribe before patching/enabling
            this.unsubscribeFromFormChanges();
            this.isPatchingFromResponse = true;
            this.receiveInventoryForm.patchValue(
              {
                serialNumber: response.data.serialNumber || response.data.serialNum || '',
                description: response.data.rawMaterial || '',
                totalAvailableStock: response.data.qtyUnit ? `${response.data.qty} ${response.data.qtyUnit}` : response.data.qty,
                quantityToReceive: 0,
                comment: response.data.comment || ''
              },
              { emitEvent: false }
            );
            this.loadingStockData = false;
            this.receiveInventoryForm.enable();
            setTimeout(() => {
              this.isPatchingFromResponse = false;
              this.subscribeToFormChanges();
            }, 0);
          } else {
            this.loadingStockData = false;
            this.receiveInventoryForm.enable();
            this.subscribeToFormChanges();
          }
        },
        error: () => {
          this.loadingStockData = false;
          this.receiveInventoryForm.enable();
          this.subscribeToFormChanges();
          // Handle error (show error message)
          if (this.selectedAction === 'receiveInventory') {
            this.alertService.showAlert({
              type: AlertType.critical,
              content: this.translationService.translate('inventoryAddErrorText'),
              timestamp: 5000
            } as Alert);
          } else if (this.selectedAction === 'adjustInventory') {
            this.alertService.showAlert({
              type: AlertType.critical,
              content: this.translationService.translate('inventoryAdjustErrorText'),
              timestamp: 5000
            } as Alert);
          }
        }
      });
    } else {
      // Mark all controls as touched to show validation errors
      this.receiveInventoryForm.markAllAsTouched();
    }
  }

  refreshForm(): void {
    // Reload metadata and reset the form to default state
    this.loadMetadata();
  }

  getPlaceHolderText(): string {
    if (this.selectedAction === 'receiveInventory') {
      return this.translationService.translate('enterPositiveAmountText');
    } else {
      return this.translationService.translate('enterAmountNegativeText');
    }
  }
  getCommentPlaceHolderText(): string {
    if (this.selectedAction === 'receiveInventory') {
      return this.translationService.translate('additionalCommentInventoryReceivedText');
    } else {
      return this.translationService.translate('additionalCommentAdjustmentText');
    }
  }
}
